import ImageWIthSignedUrl from "components/S3Utility/ImageWIthSignedUrl";
import React, { useEffect, useRef, useState } from "react";
import { getColorsArrayByIds, truncateString } from "utils/UtilityFuncs";

/**
 * The product list in the Duplicate WorkOrder dialog: one checkbox per
 * product, all ticked to start with, so the copy carries only what is left
 * ticked. Replaced a single "WO Products" checkbox (all or nothing).
 *
 * Columns are the W-Products tab's, image first. productImage arrives as an
 * S3 key (getAllWOProducts does not sign it), so the thumbnail goes through
 * ImageWIthSignedUrl -- same as the Bulk Delete list on that tab.
 *
 * `selectedIds` is a Set of WOProducts.id; `onChange` gets a new Set.
 */
export default function DuplicateWOProducts({ products, selectedIds, onChange }) {
  const [colorMap, setColorMap] = useState(null);
  const allRef = useRef(null);

  // Colour names for approval colours, looked up the way the W-Products tab
  // does it; products without approval colours show productColor.
  useEffect(() => {
    const ids = [
      ...new Set(
        products.flatMap((p) => (Array.isArray(p.approvalColors) ? p.approvalColors : [])),
      ),
    ];
    if (!ids.length) {
      setColorMap(null);
      return;
    }
    let cancelled = false;
    getColorsArrayByIds(ids)
      .then((arr) => {
        if (!cancelled) {
          setColorMap(Object.fromEntries((arr || []).map((c) => [c.colorId, c.name])));
        }
      })
      .catch(() => {
        if (!cancelled) setColorMap(null);
      });
    return () => {
      cancelled = true;
    };
  }, [products]);

  const total = products.length;
  const count = products.filter((p) => selectedIds.has(p.id)).length;

  // The header box shows "some" as well as all/none.
  useEffect(() => {
    if (allRef.current) allRef.current.indeterminate = count > 0 && count < total;
  }, [count, total]);

  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const toggleAll = (checked) => onChange(new Set(checked ? products.map((p) => p.id) : []));

  const colorOf = (p) =>
    colorMap && Array.isArray(p.approvalColors) && p.approvalColors.length > 0
      ? truncateString(
          p.approvalColors.map((id) => colorMap[parseInt(id, 10)]).join(", "),
          20,
        )
      : truncateString(p.productColor || "", 30);

  if (!total) {
    return <p className="text-muted small mb-0">This work order has no products.</p>;
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-baseline mb-1">
        <p className="mb-0">
          <span className="fw-bold">Products</span>
          <span className="text-muted small ms-2">
            untick any the copy should not include · sizes, quantities and prices are not copied
          </span>
        </p>
        <span className="small text-muted">
          {count} of {total} selected
        </span>
      </div>

      <div className="table-responsive border rounded" style={{ maxHeight: 340, overflowY: "auto" }}>
        <table className="table table-sm table-hover align-middle mb-0">
          <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
            <tr>
              <th style={{ width: 32 }}>
                <input
                  ref={allRef}
                  type="checkbox"
                  className="form-check-input"
                  aria-label="Select all products"
                  checked={count === total}
                  onChange={(e) => toggleAll(e.target.checked)}
                />
              </th>
              <th style={{ width: 84 }}>Image</th>
              <th>Item No</th>
              <th>Type</th>
              <th>Style</th>
              <th>Description</th>
              <th>Color</th>
              <th>Team Name</th>
              <th>Quantity</th>
              <th>Is Ready</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const checked = selectedIds.has(p.id);
              return (
                <tr
                  key={p.id}
                  className={checked ? "" : "text-muted"}
                  style={{ cursor: "pointer", opacity: checked ? 1 : 0.6 }}
                  // The whole row toggles; the checkbox handles its own
                  // clicks so they are not counted twice.
                  onClick={(e) => {
                    if (e.target.tagName !== "INPUT") toggle(p.id);
                  }}
                >
                  <td>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      aria-label={`Copy item ${p.sortId ?? ""} ${p.productStyle ?? ""}`.trim()}
                      checked={checked}
                      onChange={() => toggle(p.id)}
                    />
                  </td>
                  <td>
                    {p.productImage ? (
                      <ImageWIthSignedUrl
                        path={p.productImage}
                        alt={p.productDescription || "Product image"}
                        imgStyle={{ width: 70, height: 50, objectFit: "contain" }}
                      />
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center text-muted bg-light rounded"
                        style={{ width: 70, height: 50 }}
                        title="No image"
                      >
                        <i className="mdi mdi-image-off-outline"></i>
                      </div>
                    )}
                  </td>
                  <td>{p.sortId}</td>
                  <td>{p?.productTypes?.productTypes || p.productType || "No product type selected"}</td>
                  <td>{p.productStyle}</td>
                  <td>{p.productDescription}</td>
                  <td>{colorOf(p)}</td>
                  <td>{p.teamName}</td>
                  <td>{p.totalQuantity}</td>
                  <td>{p.isReady ? "Yes" : "No"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
